import { useEffect, useState } from "react";

import uniqueId from "lodash/uniqueId";
import { Search } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";
import { useMediaQuery } from "react-responsive";

import { lazyFetch } from "@/components/helpers/graphql/lazy-fetch";
import { isCoach } from "@/components/helpers/workspace/permissions";
import { isXsQuery } from "@/components/layout/Media";
import { HeaderButton } from "@/components/pieces/button/HeaderButton";
import { GlobalSearch } from "@/components/pieces/search/GlobalSearch/GlobalSearch";
import { Button } from "@/components/ui/button";
import { SearchPreferenceQuery, useSearchPreferenceLazyQuery } from "@/graphql";
import { cn } from "@/lib/utils";

let searchButtonTimeoutRef: NodeJS.Timeout;

interface SearchButtonProps {
  disabled?: boolean;
  customerId?: string;
}

export function SearchButton({ disabled, customerId }: SearchButtonProps) {
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchKey] = useState(uniqueId());
  const isXs = useMediaQuery(isXsQuery);

  const [fetch, { data: searchPreferenceData }] = useSearchPreferenceLazyQuery({
    variables: {
      customerId,
    },
  });

  useEffect(() => {
    if (!disabled) {
      if (searchButtonTimeoutRef) {
        clearTimeout(searchButtonTimeoutRef);
      }
      searchButtonTimeoutRef = lazyFetch(fetch);
      return () => clearTimeout(searchButtonTimeoutRef);
    }
  }, [disabled]);

  useEffect(() => {
    if (searchButtonTimeoutRef) {
      return () => {
        clearTimeout(searchButtonTimeoutRef);
      };
    }
  }, []);

  useHotkeys(
    "meta+k,ctrl+k",
    (e) => {
      showSearch();
      e.preventDefault();
    },
    {},
    [],
  );

  function showSearch() {
    setSearchVisible(true);
  }

  // Default search preference to avoid conditional mounting that causes hooks order issues
  const defaultSearchPreference: SearchPreferenceQuery = {
    __typename: "Query",
    customer: null,
    recentSearches: null,
    currentUser: { __typename: "UserType", id: "", isStaff: false },
  };

  return (
    <>
      {isXs ? (
        <HeaderButton icon="search" onClick={showSearch} disabled={disabled} />
      ) : (
        <Button
          size="md"
          intent="secondary"
          variant="outline"
          onClick={showSearch}
          disabled={disabled}
          className="group w-50 flex items-center justify-between text-muted-foreground h-10.5"
        >
          <div className="flex items-center">
            <Search className="size-4" />
            <span className="ml-2 font-normal">Search</span>
          </div>

          <span
            className={cn(
              "opacity-0 transition-opacity duration-150 text-muted-foreground",
              "group-hover:opacity-100",
            )}
          >
            ⌘K
          </span>
        </Button>
      )}

      <GlobalSearch
        key={searchKey}
        visible={searchVisible}
        onClose={() => setSearchVisible(false)}
        searchPreference={searchPreferenceData || defaultSearchPreference}
        isCoach={isCoach()}
      />
    </>
  );
}
